import React, { useEffect, useState } from 'react';
import { Menu, X, Phone, ArrowRight, BadgePercent, Home, Sparkles, Award, ListOrdered, Map, HelpCircle } from 'lucide-react';
import { company } from '../data/content';

const nav = [
  { id: 'start', label: 'Start', I: Home },
  { id: 'leistungen', label: 'Leistungen', I: Sparkles },
  { id: 'warum', label: 'Warum wir', I: Award },
  { id: 'prozess', label: 'Prozess', I: ListOrdered },
  { id: 'einsatzgebiet', label: 'Einsatzgebiet', I: Map },
  { id: 'faq', label: 'FAQ', I: HelpCircle },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('start');

  useEffect(() => {
    const sections = nav.map((n) => document.getElementById(n.id)).filter(Boolean);
    if (!sections.length) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: '-40% 0px -55% 0px' }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <header data-testid="site-header" className="fixed top-3 md:top-5 inset-x-0 z-50 px-3 sm:px-5">
      <div className="max-w-7xl mx-auto">
        <div className="header-pill rounded-[32px] lg:rounded-[28px] pl-2 pr-3 md:pl-4 md:pr-5 lg:px-5">
          <div className="h-[80px] md:h-[88px] flex items-center justify-between gap-3">
            <a href="/#start" className="flex items-center gap-2.5 md:gap-3 shrink-0 min-w-0" data-testid="header-logo">
              <img src="/assets/logo.png" alt="Die Haushelden" className="h-[68px] md:h-[76px] w-auto drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]" />
              <div className="leading-tight min-w-0">
                <div className="text-white font-extrabold text-[17px] sm:text-lg md:text-xl tracking-wide whitespace-nowrap">Die Haushelden</div>
                <div className="text-[#E8B547] text-[9px] sm:text-[10px] md:text-[11px] uppercase tracking-[0.16em] md:tracking-[0.2em] font-bold whitespace-nowrap">Sanierung · Reinigung · Pflege</div>
              </div>
            </a>

            <div className="flex items-center gap-2.5 md:gap-3">
              <a href="/#kontakt" className="hidden md:inline-flex items-center gap-1.5 whitespace-nowrap bg-[#DC2626] text-white text-[12px] font-extrabold uppercase tracking-wider px-3 py-2 rounded-full shadow-[0_6px_16px_rgba(220,38,38,0.45)] hover:bg-[#b91c1c] transition-colors promo-pulse" data-testid="header-promo-badge">
                <BadgePercent className="w-4 h-4" /> 30 % Neukunden
              </a>
              <a href={`tel:${company.phoneRaw}`} className="hidden lg:inline-flex items-center gap-2 whitespace-nowrap text-white/85 hover:text-white text-[15px] font-semibold transition-colors" data-testid="header-phone">
                <Phone className="w-4 h-4 text-[#E8B547]" /> {company.phone}
              </a>
              <a href="/#kontakt" className="hidden md:inline-flex items-center gap-3 whitespace-nowrap btn-gold rounded-full pl-6 pr-1.5 py-1.5 text-[15px] font-semibold" data-testid="header-cta">
                Anfragen
                <span className="w-10 h-10 rounded-full bg-[#0B1E44] text-[#E8B547] flex items-center justify-center">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </a>
              <button onClick={() => setOpen(!open)} className="lg:hidden w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-white/20 transition-colors" aria-label="Menu" data-testid="mobile-menu-toggle">
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          <nav className="hidden lg:flex items-center justify-center gap-2 xl:gap-4 border-t border-white/10 h-12 whitespace-nowrap" data-testid="header-nav">
            {nav.map((i) => (
              <a
                key={i.id}
                href={`/#${i.id}`}
                data-testid={`nav-${i.id}`}
                className={`inline-flex items-center gap-2 text-[15px] font-medium px-4 py-2 rounded-full transition-colors ${active === i.id ? 'bg-white/10 text-white' : 'text-white/75 hover:text-white hover:bg-white/5'}`}
              >
                <i.I className={`w-4 h-4 ${active === i.id ? 'text-[#E8B547]' : 'text-white/50'}`} />
                {i.label}
              </a>
            ))}
          </nav>
        </div>

        {open && (
          <div className="lg:hidden mt-2 header-pill rounded-3xl p-4" data-testid="mobile-menu">
            <div className="flex flex-col">
              {nav.map((i) => (
                <a key={i.id} href={`/#${i.id}`} onClick={() => setOpen(false)} className={`flex items-center gap-3 py-3 border-b border-white/10 font-medium transition-colors ${active === i.id ? 'text-[#E8B547]' : 'text-white/90 hover:text-[#E8B547]'}`} data-testid={`mobile-nav-${i.id}`}>
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active === i.id ? 'bg-[#E8B547] text-[#0B1E44]' : 'bg-white/8 border border-white/10 text-[#E8B547]'}`}>
                    <i.I className="w-4 h-4" />
                  </span>
                  {i.label}
                </a>
              ))}
              <a href="/#kontakt" onClick={() => setOpen(false)} className="mt-4 inline-flex items-center justify-center gap-2 bg-[#DC2626] text-white text-xs font-extrabold uppercase tracking-wider px-4 py-2.5 rounded-full">
                <BadgePercent className="w-4 h-4" /> 30 % Neukunden-Rabatt sichern
              </a>
              <a href="/#kontakt" onClick={() => setOpen(false)} className="mt-3 btn-gold rounded-full px-5 py-3 text-center inline-flex items-center justify-center gap-2 font-semibold">
                Anfragen <ArrowRight className="w-4 h-4" />
              </a>
              <a href={`tel:${company.phoneRaw}`} className="mt-3 text-center text-white/85 inline-flex items-center justify-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-[#E8B547]" /> {company.phone}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
